import {
  IStory, IStoryQuestionGroup, LANGUAGE_OPTIONS, reduxProps,
  storyInitialValues, storyMetaInitialValues, storyQuestionGroupInitialValues,
} from '../../constants'
import {EDUCATION_REQUEST_URL} from '../../constants/environments'
import {Modal} from 'react-bootstrap'
import React, {useEffect, useState} from 'react'
import {actionQuestionDetailModalOpen, actionStoryDetailModalClose, actionStoryReadingModalOpen} from '../../redux/modal'
import {useDispatch, useSelector} from 'react-redux'
import {IStoryMeta} from '../../constants/types/education/model/story-meta.model'
import {actionGetStoryMeta} from '../../redux/education'
import {actionGetStoryDetail} from '../../redux/education/actions/story.action'
import {actionGetStoryQuestionGroup} from '../../redux/question'
import {UiIcon} from '../UiIcon'
import {storyCoverUrl} from '../../education/story/story-item'
import './StoryDetail.scss'
import {StoryActivities} from '../../education/activity/StoryActivities'

export function StoryDetailModal() {
  const dispatch = useDispatch()
  const {storyDetailModalOpen, storyDetailModalField} = useSelector((state: reduxProps) => state.modalReducer)
  const {categories, stories} = useSelector((state: reduxProps) => state.educationReducer)
  const {StoryId, StoryDetailId, LanguageCode} = storyDetailModalField
  const [storyDetail, setStoryDetail] = useState<IStory>(storyInitialValues)
  const [storyMeta, setStoryMeta] = useState<IStoryMeta>(storyMetaInitialValues)
  const [questionGroup, setQuestionGroup] = useState<IStoryQuestionGroup>(storyQuestionGroupInitialValues)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [metaLoading, setMetaLoading] = useState(true)
  const [metaError, setMetaError] = useState(false)
  const [questionsLoading, setQuestionsLoading] = useState(true)
  const [questionsError, setQuestionsError] = useState(false)
  const [coverError, setCoverError] = useState(false)
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    if (!storyDetailModalOpen || !StoryId) return
    let active = true
    setStoryDetail(storyInitialValues)
    setStoryMeta(storyMetaInitialValues)
    setQuestionGroup(storyQuestionGroupInitialValues)
    setLoading(true)
    setMetaLoading(true)
    setQuestionsLoading(true)
    setError(false)
    setMetaError(false)
    setQuestionsError(false)
    setCoverError(false)
    const request = {storyId: StoryId, languageCode: LanguageCode}
    dispatch(actionGetStoryDetail(request, result => {
      if (!active) return
      setLoading(false)
      if (result.type === 'SUCCESS' && result.payload?.data?.storyId) setStoryDetail(result.payload.data)
      else setError(true)
    }))
    dispatch(actionGetStoryMeta(request, result => {
      if (!active) return
      setMetaLoading(false)
      if (result.type === 'SUCCESS' && result.payload?.data) setStoryMeta(result.payload.data)
      else setMetaError(result.payload?.data?.errorName !== 'NotFound')
    }))
    dispatch(actionGetStoryQuestionGroup(request, result => {
      if (!active) return
      setQuestionsLoading(false)
      if (result.type === 'SUCCESS' && result.payload?.data) setQuestionGroup(result.payload.data)
      else setQuestionsError(result.payload?.data?.errorName !== 'NotFound')
    }))
    return () => {active = false}
  }, [dispatch, storyDetailModalOpen, StoryId, LanguageCode, retry])

  const handleClose = () => dispatch(actionStoryDetailModalClose())
  const reload = () => setRetry(value => value + 1)
  const ready = !loading && !error && String(storyDetail.storyId) === String(StoryId)
  const categoryNames = categories.filter(item => storyDetail.categoryIds?.includes(item.id)).map(item => item.defaultTitle)
  const catalogStory = stories.find(item => String(item.storyId) === String(StoryId) && item.languageCode === LanguageCode)
  const coverStory = catalogStory?.thumbnail ? catalogStory : storyDetail
  const cover = coverStory.thumbnail ? storyCoverUrl(coverStory) : storyDetail.tabletCover ? `${EDUCATION_REQUEST_URL}/story/content/${storyDetail.tabletCover}` : ''
  const language = LANGUAGE_OPTIONS.find(item => item.value === LanguageCode)?.label || LanguageCode
  const metadata = [
    {label: 'Kategoriler', values: categoryNames},
    {label: 'Sınıf seviyesi', values: (storyMeta.gradeLevels || []).map(value => /^[1-9]\d*$/.test(String(value)) ? `${value}. sınıf` : value)},
    {label: 'Kavramlar', values: storyMeta.terms || []},
    {label: 'PYP temaları', values: storyMeta.pypThemes || []},
  ].filter(item => item.values.length)

  function openStoryReadingModal() {
    dispatch(actionStoryReadingModalOpen({StoryId, LanguageCode, StoryDetailId: StoryDetailId || storyDetail.storyDetailId}))
  }

  function openStoryQuestions() {
    dispatch(actionQuestionDetailModalOpen({StoryId, LanguageCode, ShowType: 'StoryDetailModal'}))
  }

  return <Modal show={storyDetailModalOpen} onHide={handleClose} size='lg' centered scrollable className='book-preview-modal' aria-labelledby='book-preview-title'>
    <Modal.Header className='book-preview-header'>
      <div><span className='book-preview-heading-icon'><UiIcon name='book' size={20}/></span><span>Kitap detayları</span></div>
      <button type='button' className='book-preview-close' aria-label='Kitap detayını kapat' onClick={handleClose}><UiIcon name='close' size={20}/></button>
    </Modal.Header>
    <Modal.Body tabIndex={0} role='region' aria-label='Kitap bilgileri'>
      {!ready ? <div className='book-preview-state' role={error ? 'alert' : 'status'}>
        <span className='book-preview-state-icon'><UiIcon name='book' size={30}/></span>
        <h2 id='book-preview-title'>{error ? 'Kitap bilgileri yüklenemedi' : 'Kitap yükleniyor…'}</h2>
        <p>{error ? 'Lütfen tekrar deneyin.' : 'Hikâye ve eğitim içeriği hazırlanıyor.'}</p>
        {error && <button type='button' className='book-preview-button' onClick={reload}>Tekrar dene</button>}
      </div> : <>
        <div className='book-preview-overview'>
          <div className='book-preview-cover'>
            {!coverError && cover ? <img src={cover} alt={`${storyDetail.title} kitap kapağı`} onError={() => setCoverError(true)}/>
              : <div className='book-preview-cover-fallback'><UiIcon name='book' size={40}/><span>Kapak görseli yok</span></div>}
          </div>
          <div className='book-preview-intro'>
            <span className='book-preview-eyebrow'>BİRLİKTE OKUYALIM</span>
            <h2 id='book-preview-title'>{storyDetail.title}</h2>
            {storyDetail.author && <p className='book-preview-author'>{storyDetail.author}</p>}
            <div className='book-preview-badges'>
              <span>{language}</span>
              {!questionsLoading && !questionsError && questionGroup.questionCount > 0 && <span><UiIcon name='check' size={14}/>Değerlendirme soruları</span>}
            </div>
          </div>
          {storyDetail.description && <section className='book-preview-description' aria-labelledby='book-preview-about'><h3 id='book-preview-about'>Hikâye hakkında</h3><p>{storyDetail.description}</p></section>}
        </div>
        {metaLoading ? <p className='book-preview-notice' role='status'>Eğitim bilgileri yükleniyor…</p> : metaError ? <div className='book-preview-notice' role='alert'>Eğitim bilgileri alınamadı.<button type='button' onClick={reload}>Tekrar dene</button></div> : <>
          {metadata.length > 0 && <section className='book-preview-education' aria-labelledby='book-preview-education-title'>
            <h3 id='book-preview-education-title'>Eğitim içeriği</h3>
            <dl className='book-preview-metadata'>{metadata.map(item => <div key={item.label}><dt>{item.label}</dt><dd>{item.values.map((value, index) => <span key={`${value}-${index}`}>{value}</span>)}</dd></div>)}</dl>
          </section>}
          {storyMeta.activity && <section className='book-preview-activity' aria-labelledby='book-preview-activity-title'><span className='book-preview-activity-icon'><UiIcon name='people' size={21}/></span><div><h3 id='book-preview-activity-title'>Etkinlik önerisi</h3><p>{storyMeta.activity}</p></div></section>}
        </>}
        {storyDetailModalOpen && <StoryActivities key={StoryId} storyId={StoryId} onOpen={handleClose}/>}
      </>}
    </Modal.Body>
    <Modal.Footer className='book-preview-footer'>
      <button type='button' className='book-preview-button is-quiet' onClick={handleClose}>Kapat</button>
      <div className='book-preview-actions'>
        {ready && (questionsError ? <button type='button' className='book-preview-button' onClick={reload}>Soruları yeniden yükle</button> : questionsLoading ? <span className='book-preview-notice' role='status'>Sorular yükleniyor…</span> : questionGroup.questionCount > 0 && <button type='button' className='book-preview-button' onClick={openStoryQuestions}><UiIcon name='check' size={17}/>Cevap anahtarı</button>)}
        <button type='button' className='book-preview-button is-primary' disabled={!ready} onClick={openStoryReadingModal}><UiIcon name='book' size={18}/>Hikâyeyi oku<UiIcon name='arrow' size={15}/></button>
      </div>
    </Modal.Footer>
  </Modal>
}
